import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  type EmbeddedViewRef,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
  PLATFORM_ID,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { type Placement } from '@floating-ui/dom';
import { faAngleLeft, faAngleRight, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ButtonComponent, ButtonIconOnlyComponent } from '../../../button';
import { PopoverInstance } from '../../../popover/popover-instance';
import { POPOVER_VARIATIONS } from '../../../popover/popover-variations';
import { TourService } from '../../tour.service';

@Component({
  selector: 'rcp-tour-step-display',
  templateUrl: './tour-step-display.component.html',
  styleUrls: ['./tour-step-display.component.scss'],
  imports: [TranslatePipe, ButtonIconOnlyComponent, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TourStepDisplayComponent implements OnInit, OnDestroy {
  private tourService = inject(TourService);
  private router = inject(Router);
  private viewContainerRef = inject(ViewContainerRef);
  private platformId = inject(PLATFORM_ID);

  @Input() id: string;
  @Input() attachToElement: HTMLElement;
  @Input() position: Placement;
  @Input() sticky = false;

  tourStepNavigate = toSignal(this.watchTourStepNavigate());
  shouldShow = computed(() => this.tourStepNavigate()?.tourStep.id === this.id);

  destroyed$: Subject<void> = new Subject();

  tourStepContent = viewChild('tourStepContent', { read: TemplateRef });

  private tourStep: PopoverInstance | undefined;
  private embeddedView: EmbeddedViewRef<unknown> | null = null;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        const content = this.tourStepContent(),
          shouldShow = this.shouldShow();

        untracked(() => {
          if (content && shouldShow) {
            this.showTourStep(content);
            this.tourStep?.show();
          } else {
            this.tourStep?.hide();
          }
        });
      });
    }
  }

  ngOnInit() {
    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((event) => {
      // hide tour steps when navigating away
      // don't store the dismiss since user might want to come back to read (it's not a conscious decision to dismiss)
      if (event instanceof NavigationStart) {
        this.dismissWithoutStore();
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.tourStep?.destroy();
    this.embeddedView?.destroy();
  }

  dismissAll() {
    this.dismissSelf();
    this.tourService.unregisterAll();
  }

  dismissSelf() {
    this.tourService.storeTourItemDismissed(this.id).subscribe();
    this.tourStep?.hide();
  }

  previousStep() {
    this.tourService.previousTourStep();
  }

  nextStep() {
    this.tourService.storeTourItemDismissed(this.id).subscribe();
    this.tourService.nextTourStep();
  }

  private showTourStep(content: TemplateRef<unknown>) {
    this.tourStep?.destroy();
    this.embeddedView?.destroy();

    this.tourStep = new PopoverInstance(
      this.attachToElement,
      {
        ...POPOVER_VARIATIONS.default,
        variationName: 'default',
        trigger: 'manual',
        interactive: true,
        placement: this.position ?? 'top',
      },
      () => {
        this.embeddedView?.destroy();
        this.embeddedView = this.viewContainerRef.createEmbeddedView(content);
        this.embeddedView.detectChanges();
        return this.embeddedView.rootNodes as Node[];
      },
    );
  }

  private dismissWithoutStore() {
    this.tourStep?.hide();
  }

  private watchTourStepNavigate() {
    return this.tourService.watchTourStepNavigate();
  }

  protected readonly faTimes = faTimes;
  protected readonly faAngleLeft = faAngleLeft;
  protected readonly faAngleRight = faAngleRight;
}
